import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Users, ArrowRight, Star, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvitedAthletes = () => {
  const { getAuthHeaders } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/coach/invited-athletes`, { headers: getAuthHeaders() });
        setAthletes(res.data);
      } catch (error) {
        console.error('Failed to fetch invited athletes:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invited-athletes">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight">Invited Athletes</h1>
        <p className="text-muted-foreground text-sm md:text-base">
          Athletes who have granted you access via Athlete Pass Plus
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Star className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-sm">
            <p className="font-medium text-blue-400 mb-1">Athlete Pass Plus Access</p>
            <p className="text-muted-foreground">
              These athletes have invited you to view their development history. You have limited access
              to view progress, add notes, and provide feedback. To become their primary coach, the athlete
              must initiate a coach transition.
            </p>
          </div>
        </div>
      </div>

      {/* Athletes List */}
      {athletes.length === 0 ? (
        <div className="bg-card border border-border/40 rounded-sm p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="font-heading text-lg mb-2">No Invited Athletes</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Athletes with Athlete Pass Plus can invite you to view their development history.
          </p>
          <p className="text-xs text-muted-foreground">
            Share your email with athletes or parents who want to grant you access.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {athletes.map((athlete) => (
            <Link 
              key={athlete.id}
              to={`/coach/athlete-workspace/${athlete.id}`}
              className="bg-card border border-border/40 rounded-sm p-4 md:p-6 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold text-xl">{athlete.name[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-lg truncate">{athlete.name}</h3>
                    <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">
                      Invited
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {athlete.position} • {athlete.age} years • {athlete.sport}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Primary Coach: {athlete.primary_coach_name}</span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      Invited {new Date(athlete.invitation_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground group-hover:text-primary transition-colors" size={20} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvitedAthletes;
